import { divisionTables, slotContext } from "./standings";
import { teamRecord, tournamentPlacements } from "./team-record";
import {
  type Division,
  gameSlots,
  type Pool,
  resolveSlot,
  type ScheduledGame,
  type Team,
  UNASSIGNED_DIVISION,
} from "./types";

export interface PodiumRow {
  divisionId: string;
  team: Team;
  place: number;
}

/** Public honours use completed divisions, never provisional pool leaders.
 * Brackets must decide each finish; separate pools cannot decide an overall podium. */
export function tournamentPodium(input: {
  teams: Team[];
  schedule: ScheduledGame[];
  divisions: Division[];
  pools: Pool[];
}): PodiumRow[] {
  const ctx = slotContext(input.teams, input.schedule);
  const rows: PodiumRow[] = [];
  for (const divisionId of new Set(input.teams.map((team) => team.divisionId))) {
    const teams = input.teams.filter((team) => team.divisionId === divisionId);
    const ids = new Set(teams.map((team) => team.id));
    const schedule = input.schedule.filter((game) => {
      if (game.divisionId !== undefined) return game.divisionId === divisionId;
      const slots = gameSlots(game);
      return (
        ids.has(resolveSlot(slots.a, ctx)?.id ?? "") &&
        ids.has(resolveSlot(slots.b, ctx)?.id ?? "")
      );
    });
    if (schedule.length === 0 || schedule.some((game) => game.result === undefined)) continue;
    const bracket = schedule.some((game) => game.stage === "bracket");
    const divisionInput = { ...input, schedule };
    if (
      !bracket &&
      (divisionId === undefined ||
        divisionTables(teams, schedule, divisionId, input.pools).length !== 1)
    ) continue;
    const placements = bracket ? null : tournamentPlacements(divisionInput);
    for (const team of teams) {
      const place = bracket
        ? teamRecord({ ...divisionInput, teamId: team.id })?.placement
        : placements?.get(team.id);
      if (place != null && place >= 1 && place <= 3) {
        rows.push({ divisionId: divisionId ?? UNASSIGNED_DIVISION, team, place });
      }
    }
  }
  return rows.sort((a, b) => a.place - b.place);
}

/** The event can lead with honours only when every participating division has
 * its final podium. A two-team division needs two places; empty setup divisions
 * do not block a completed event. Archived-but-unfinished schedules still fail. */
export function hasCompletePodiums(
  teams: readonly Team[],
  schedule: readonly ScheduledGame[],
  podium: readonly PodiumRow[],
): boolean {
  if (teams.length === 0 || schedule.length === 0 || schedule.some((game) => game.result === undefined))
    return false;
  const divisions = new Map<string, Team[]>();
  for (const team of teams) {
    const id = team.divisionId ?? UNASSIGNED_DIVISION;
    const members = divisions.get(id) ?? [];
    members.push(team);
    divisions.set(id, members);
  }
  return [...divisions].every(([divisionId, members]) => {
    const ids = new Set(members.map((team) => team.id));
    const places = new Set(podium.filter((row) => row.divisionId === divisionId && ids.has(row.team.id)).map((row) => row.place));
    return Array.from({ length: Math.min(3, members.length) }, (_, index) => index + 1)
      .every((place) => places.has(place));
  });
}
